import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Instagram, Facebook, Youtube, ArrowUp, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';
import '../styles/scroll-animations.css';

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const Footer = () => {
  const { footerData, contactData, navigationData } = useLanguage();
  const currentYear = new Date().getFullYear();

  const handleScrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleNavClick = (href) => {
    if (window.location.pathname !== '/') { window.location.href = '/' + href; return; }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const socialIcons = { instagram: Instagram, facebook: Facebook, youtube: Youtube };

  return (
    <footer
      className="relative overflow-hidden grain-overlay"
      style={{ background: 'var(--bg-footer)', color: 'white', paddingTop: '5rem', paddingBottom: '3rem' }}
    >
      {/* Decorative blob */}
      <div style={{
        position: 'absolute', top: '-20%', left: '-10%',
        width: 400, height: 400, borderRadius: '50%',
        background: 'rgba(184, 134, 11, 0.06)',
        filter: 'blur(60px)', pointerEvents: 'none',
      }} />

      {/* Animated top gold border */}
      <motion.div
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 2,
          background: 'linear-gradient(90deg, transparent, var(--accent-gold-400), var(--accent-bronze-400), transparent)',
          backgroundSize: '200% 100%',
        }}
        animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
      />

      <div className="container">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-14"
        >
          {/* Brand */}
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} style={{ color: 'var(--accent-gold-400)' }} />
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem', fontWeight: 600 }}>
                {navigationData.logo}
              </h3>
            </div>
            <p className="body-small mb-6" style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
              {footerData.tagline}
            </p>
            <div className="flex gap-2">
              {Object.entries(contactData.social).map(([platform, handle], i) => {
                const Icon = socialIcons[platform];
                return (
                  <motion.a
                    key={platform}
                    href={`https://${platform}.com/${handle.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={platform}
                    style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.08)', color: 'white', textDecoration: 'none' }}
                    whileHover={{ scale: 1.15, y: -3, background: 'rgba(184,134,11,0.3)' }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                  >
                    <Icon size={15} />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={fadeUp}>
            <h4 className="mono-text mb-5" style={{ color: 'var(--accent-gold-400)' }}>Links Rápidos</h4>
            <ul className="space-y-3">
              {footerData.quickLinks.map((link, i) => (
                <li key={i}>
                  <motion.a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                    className="body-small"
                    style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    whileHover={{ x: 5, color: 'rgba(255,255,255,1)' }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  >
                    <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--accent-gold-400)', flexShrink: 0, display: 'inline-block' }} />
                    {link.label}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Navigation */}
          <motion.div variants={fadeUp}>
            <h4 className="mono-text mb-5" style={{ color: 'var(--accent-gold-400)' }}>Navegação</h4>
            <ul className="space-y-3">
              {navigationData.links.slice(0, 5).map((link, i) => (
                <li key={i}>
                  <motion.a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                    className="body-small"
                    style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    whileHover={{ x: 5, color: 'rgba(255,255,255,1)' }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  >
                    <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--accent-gold-400)', flexShrink: 0, display: 'inline-block' }} />
                    {link.label}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div variants={fadeUp}>
            <h4 className="mono-text mb-5" style={{ color: 'var(--accent-gold-400)' }}>Contato</h4>
            <ul className="space-y-3">
              {[contactData.email, contactData.phone, contactData.address].map((item, i) => (
                <li key={i}>
                  <p className="body-small" style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{item}</p>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>

        {/* Gold divider */}
        <motion.div
          style={{ height: 1, background: 'rgba(184,134,11,0.25)', marginBottom: '2rem' }}
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        />

        {/* Bottom Row */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="caption flex flex-wrap items-center gap-1"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            © {currentYear} {navigationData.logo}. Feito com{' '}
            <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ repeat: Infinity, duration: 1.5 }}>
              <Heart size={11} style={{ color: '#F28B82', display: 'inline' }} />
            </motion.span>{' '}
            e fé.
            <span style={{ margin: '0 6px' }}>•</span>
            <a href="https://tomich.com.br" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}
              onMouseEnter={e => e.target.style.color = 'rgba(255,255,255,0.8)'}
              onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.4)'}
            >
              Desenvolvido por Tomich Tecnologia
            </a>
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            onClick={handleScrollTop}
            className="flex items-center gap-2"
            style={{ color: 'rgba(255,255,255,0.5)', background: 'none', border: 'none', fontFamily: 'Inter, sans-serif', fontSize: '0.75rem' }}
            whileHover={{ color: 'rgba(255,255,255,0.9)', y: -2 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          >
            <span>Voltar ao topo</span>
            <motion.div
              style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.15)' }}
              whileHover={{ borderColor: 'var(--accent-gold-400)', background: 'rgba(184,134,11,0.15)' }}
              animate={{ y: [0, -3, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            >
              <ArrowUp size={14} />
            </motion.div>
          </motion.button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
